'use client';

import React from 'react';
import {
  getTourFeedbackSuccessVariant,
  TOUR_FEEDBACK_ISSUES,
  type TourFeedbackIssueValue,
  type TourFeedbackPrompt as TourFeedbackPromptData,
  type TourFeedbackSuccessVariant,
} from '@repo/utils';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Button } from '@/components/ui/Button';
import { Divider } from '@/components/ui/Divider';
import { FilterChip } from '@/components/ui/FilterChip';
import { Modal } from '@/components/ui/Modal';
import { StarRating } from '@/components/ui/StarRating';
import { TextArea } from '@/components/ui/TextArea';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/useDeviceType';
import { useStore } from '@/providers/StoreProvider';
import { TourFeedbackPropertyCard } from './TourFeedbackPropertyCard';
import { TourFeedbackSuccess } from './TourFeedbackSuccess';

function TourFeedbackSheetContent({
  prompt,
  isSubmitting,
  onSubmit,
}: {
  prompt: TourFeedbackPromptData;
  isSubmitting: boolean;
  onSubmit: (payload: {
    rating: 1 | 2 | 3 | 4 | 5;
    issues: TourFeedbackIssueValue[];
    comment: string;
  }) => void;
}) {
  const analytics = useAnalytics();
  const [rating, setRating] = React.useState(0);
  const [issues, setIssues] = React.useState<TourFeedbackIssueValue[]>([]);
  const [comment, setComment] = React.useState('');

  const toggleIssue = (value: TourFeedbackIssueValue) => {
    analytics.track('tour_feedback_issue', { val: value });
    setIssues(prev =>
      prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value],
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <TourFeedbackPropertyCard prompt={prompt} />
      <Divider />

      <div className="flex justify-center">
        <StarRating
          rating={rating}
          isStatic={false}
          numberOfStars={5}
          starSize={32}
          starSpace={12}
          outlineEmpty
          onRatingChange={nextRating => {
            const normalized = Math.max(1, Math.min(5, Math.round(nextRating))) as
              | 1
              | 2
              | 3
              | 4
              | 5;
            analytics.track('tour_feedback_star', { rating: normalized });
            setRating(normalized);
          }}
        />
      </div>

      {rating > 0 && rating < 5 ? (
        <>
          <div className="flex flex-col gap-2">
            <p className="text-body-2-high text-secondary">
              {rating === 4 ? 'Were there any issues?' : 'What issues did you have?'}
            </p>
            <div className="flex flex-wrap gap-2">
              {TOUR_FEEDBACK_ISSUES.map(issue => (
                <FilterChip
                  key={issue.value}
                  label={issue.label}
                  size="large"
                  showTrailingIcon={false}
                  boldWhenActive={false}
                  active={issues.includes(issue.value)}
                  onClick={() => toggleIssue(issue.value)}
                />
              ))}
            </div>
          </div>

          {issues.length > 0 ? (
            <TextArea
              label="Anything else you'd like to share?"
              placeholder="Tell us more..."
              value={comment}
              onChange={event => setComment(event.target.value)}
              onFocus={() => analytics.track('tour_feedback_text')}
              rows={4}
            />
          ) : null}
        </>
      ) : null}

      <Button
        fullWidth
        disabled={rating < 1 || (rating <= 3 && issues.length === 0) || isSubmitting}
        isLoading={isSubmitting}
        onClick={() => {
          analytics.track('tour_feedback_submit', { rating: rating as 1 | 2 | 3 | 4 | 5 });
          onSubmit({
            rating: rating as 1 | 2 | 3 | 4 | 5,
            issues,
            comment: comment.trim(),
          });
        }}
      >
        Submit
      </Button>
    </div>
  );
}

export function TourFeedbackPrompt() {
  const activePrompt = useStore(s =>
    s.chat.tourFeedbackGap ? null : s.chat.tourFeedbackPrompts[0] ?? null,
  );
  const isSubmitting = useStore(s => s.chat.tourFeedbackIsSubmitting);
  const dismissActiveTourFeedbackPrompt = useStore(s => s.chat.dismissActiveTourFeedbackPrompt);
  const submitActiveTourFeedbackPrompt = useStore(s => s.chat.submitActiveTourFeedbackPrompt);
  const analytics = useAnalytics();

  const isMobile = useIsMobile();

  // After a successful submit the prompt is removed from the queue, so we keep
  // a local copy here to render the success screen in the same sheet/modal.
  const [success, setSuccess] = React.useState<{
    prompt: TourFeedbackPromptData;
    variant: TourFeedbackSuccessVariant;
  } | null>(null);

  // A single backdrop click can trigger both onOpenChange(false) and onClose,
  // so guard to only track the event and advance the queue once per prompt.
  const closingRef = React.useRef(false);
  React.useEffect(() => {
    closingRef.current = false;
  }, [activePrompt?.id]);

  const handleClose = React.useCallback(() => {
    // Closing the success screen just clears it; the prompt is already gone.
    if (success) {
      setSuccess(null);
      return;
    }
    // Ignore the programmatic close that fires while hidden during the gap;
    // only react to a real close of the shown prompt.
    if (!activePrompt) return;
    if (closingRef.current) return;
    closingRef.current = true;
    analytics.track('tour_feedback_close');
    void dismissActiveTourFeedbackPrompt();
  }, [success, activePrompt, analytics, dismissActiveTourFeedbackPrompt]);

  let content: React.ReactNode = null;
  if (success) {
    content = (
      <TourFeedbackSuccess
        prompt={success.prompt}
        variant={success.variant}
        onDone={() => setSuccess(null)}
      />
    );
  } else if (activePrompt) {
    content = (
      <TourFeedbackSheetContent
        key={activePrompt.id}
        prompt={activePrompt}
        isSubmitting={isSubmitting}
        onSubmit={async payload => {
          const submitted = activePrompt;
          const ok = await submitActiveTourFeedbackPrompt({
            tourId: submitted.id,
            tourRating: payload.rating,
            issues: payload.issues,
            comments: payload.comment,
          });
          if (ok) {
            setSuccess({
              prompt: submitted,
              variant: getTourFeedbackSuccessVariant({
                rating: payload.rating,
                issues: payload.issues,
              }),
            });
          }
        }}
      />
    );
  }

  const open = Boolean(activePrompt) || Boolean(success);

  // Mobile → bottom sheet. Tablet/desktop → centered modal (same content).
  if (!isMobile) {
    return (
      <Modal
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) handleClose();
        }}
        label="How was your tour?"
        onClose={handleClose}
        closeOnOverlayClick
        maxWidth="md"
        overlayClassName="z-[200]"
        contentClassName="z-[200]"
        bodyClassName="px-6 py-4"
      >
        {content}
      </Modal>
    );
  }

  return (
    <BottomSheet
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) handleClose();
      }}
      label="How was your tour?"
      onClose={handleClose}
      closeOnOverlayClick
      overlayClassName="z-[200] bg-black/50"
      contentClassName="z-[200]"
      footerClassName="px-6 pb-6"
      bodyClassName="px-6 py-4"
      footer={null}
    >
      {content}
    </BottomSheet>
  );
}
